import React, { useCallback, useMemo, useState } from "react";
import { Button, CircularProgress, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SaveIcon from "@material-ui/icons/Save";
import { Alert } from "@material-ui/lab";
import { ActiveStatus, CollapsiblePanel } from "src/apps/commons/dataDisplays";
import { SimpleSubTable } from "src/apps/commons/form";
import { useListSubFormState } from "src/apps/commons/form/hooks";
import useApiAlert from "src/hooks/useApiAlert";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import { toDropdown } from "src/utils";
import * as Api from "../api";
import { useFullData } from "../hooks";
import {
  IRemyndScrapeCategoryMappingRuleBatchStoreReq,
  IRemyndScrapeCategoryMappingRuleSaveReqTemp
} from "../types";
import MappingRuleModal from "./MappingRuleModal";
import PreviewContainers from "./PreviewContainers";

const useStyles = makeStyles(_ => ({
  layoutButton: {
    flex: "1 1 auto"
  }
}));

// ---------------------------------------------------------------------------

type EditModalState = Readonly<{
  open: boolean;
  index?: number;
  data?: IRemyndScrapeCategoryMappingRuleSaveReqTemp;
}>;

const ListContainers: React.FC<unknown> = _ => {
  const classes = useStyles();
  const { setMessage } = useApiAlert();
  const { handleError } = useHttpErrorHandler();

  const {
    state: { data, fetching },
    fetchFullData: fetchData
  } = useFullData();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [dirty, setDirty] = useState<boolean>(false);

  // -- Dialogs --------------------------------------------------------------

  const initialFormData = useMemo<
    IRemyndScrapeCategoryMappingRuleSaveReqTemp[]
  >(() => {
    setDirty(false);

    if (!data) return [];

    const {
      remyndCategoryLv1Map: catLv1Map,
      remyndCategoryLv2Map: catLv2Map,
      remyndCategoryLv3Map: catLv3Map,
      remyndBrandMap: brandMap
    } = data;
    return data.rules.map(rule => ({
      ...rule,
      categoryLv1: rule.categoryLv1Id
        ? toDropdown(catLv1Map[rule.categoryLv1Id])
        : null,
      categoryLv2: rule.categoryLv2Id
        ? toDropdown(catLv2Map[rule.categoryLv2Id])
        : null,
      categoryLv3: rule.categoryLv3Id
        ? toDropdown(catLv3Map[rule.categoryLv3Id])
        : null,
      brands: rule.brandIds.map(brandId => brandMap[brandId])
    }));
  }, [data]);

  const {
    addItem: addRule,
    setItem: setRule,
    delItem: delRule,
    moveItem: moveRule,
    items: rules
  } = useListSubFormState<IRemyndScrapeCategoryMappingRuleSaveReqTemp>(
    initialFormData
  );
  const [editModal, setEditModal] = useState<EditModalState>({
    open: false
  });
  const handleCloseEditModal = () => {
    setEditModal({ open: false, index: undefined, data: undefined });
  };
  const handleSubmitEditModal = (
    req: IRemyndScrapeCategoryMappingRuleSaveReqTemp
  ) => {
    setDirty(true);
    if (editModal.index !== undefined) {
      setRule(req, editModal.index);
    } else {
      addRule(req);
    }
    handleCloseEditModal();
  };
  const handleMoveRuleUp = (idx: number) => {
    setDirty(true);
    if (idx > 0) moveRule(idx, idx - 1);
  };
  const handleMoveRuleDown = (idx: number) => {
    setDirty(true);
    if (idx < rules.length - 1) moveRule(idx, idx + 1);
  };

  // -- APIs -----------------------------------------------------------------

  const callSubmit = useCallback(
    async (req: IRemyndScrapeCategoryMappingRuleBatchStoreReq) => {
      setSubmitting(true);
      let success = true;
      try {
        await Api.batchStore(req);
        setMessage("success", `บันทึก Mapping Rules สำเร็จ`);
        await fetchData();
      } catch (e) {
        handleError(e);
        success = false;
      } finally {
        setSubmitting(false);
      }
      return success;
    },
    [handleError, fetchData, setMessage]
  );

  const handleTransformAndSubmit = async () => {
    const success = await callSubmit({
      rules: rules.map(it => ({
        id: it.id,
        ord: it.ord,
        enabled: it.enabled,
        regexTh: it.regexTh,
        regexEn: it.regexEn,
        categoryLv1Id: it.categoryLv1?.value,
        categoryLv2Id: it.categoryLv2?.value,
        categoryLv3Id: it.categoryLv3?.value,
        brandIds: it.brands?.map(b => b.id)
      }))
    });
    if (success) {
      await fetchData();
    }
  };

  // -------------------------------------------------------------------------

  const ActiveCell = useCallback(
    (data: IRemyndScrapeCategoryMappingRuleSaveReqTemp) => (
      <ActiveStatus value={data.enabled} />
    ),
    []
  );
  const RegexCell = useCallback(
    (data: IRemyndScrapeCategoryMappingRuleSaveReqTemp) => (
      <div>
        <code>TH: {data.regexTh}</code>
        <br />
        <code>EN: {data.regexEn}</code>
      </div>
    ),
    []
  );
  const BrandCell = useCallback(
    (req: IRemyndScrapeCategoryMappingRuleSaveReqTemp) => {
      const { brands } = req;
      if (brands.length === 0) {
        return <span style={{ opacity: 0.6 }}>{"<ทุกแบรนด์>"}</span>;
      }
      return (
        <div>
          <ul style={{ paddingLeft: 8 }}>
            {brands.map(brand => (
              <li key={brand.id}>{`${brand.nameTh} (${brand.nameEn})`}</li>
            ))}
          </ul>
        </div>
      );
    },
    []
  );

  return (
    <>
      <CollapsiblePanel
        title="ตั้งค่า Mapping Rules สำหรับหมวดสินค้า Scrape"
        defaultCollapse={false}
      >
        <Grid container spacing={2}>
          {dirty && (
            <Grid item xs={12}>
              <Alert severity="info">
                แก้ไขรายการแล้ว ต้องกด Save All ข้างล่างด้วยถึงจะบันทึกลง
                Database
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <SimpleSubTable<IRemyndScrapeCategoryMappingRuleSaveReqTemp>
              data={rules}
              onClickAdd={() => setEditModal({ open: true })}
              onClickEdit={(req, idx) =>
                setEditModal({
                  open: true,
                  index: idx,
                  data: req
                })
              }
              onClickDelete={(_, idx) => delRule(idx)}
              onMoveUp={handleMoveRuleUp}
              onMoveDown={handleMoveRuleDown}
              showIndex
              columns={[
                {
                  label: "Enabled",
                  width: 80,
                  align: "center",
                  renderCell: ActiveCell
                },
                {
                  label: "Regex",
                  minWidth: 300,
                  style: { paddingTop: 4, paddingBottom: 4 },
                  renderCell: RegexCell
                },
                {
                  label: "ใช้กับแบรนด์",
                  minWidth: 180,
                  style: { paddingTop: 4, paddingBottom: 4 },
                  renderCell: BrandCell
                },
                {
                  label: "หมวดสินค้า Lv1",
                  minWidth: 200,
                  // eslint-disable-next-line react/display-name
                  renderCell: req =>
                    req.categoryLv1?.label ?? (
                      <span style={{ opacity: 0.6 }}>{"<null>"}</span>
                    )
                },
                {
                  label: "หมวดสินค้า Lv2",
                  minWidth: 200,
                  // eslint-disable-next-line react/display-name
                  renderCell: req =>
                    req.categoryLv2?.label ?? (
                      <span style={{ opacity: 0.6 }}>{"<null>"}</span>
                    )
                },
                {
                  label: "หมวดสินค้า Lv3",
                  minWidth: 200,
                  // eslint-disable-next-line react/display-name
                  renderCell: req =>
                    req.categoryLv3?.label ?? (
                      <span style={{ opacity: 0.6 }}>{"<null>"}</span>
                    )
                }
              ]}
            />
          </Grid>

          <Grid item className={classes.layoutButton} />
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              size="large"
              type="button"
              startIcon={
                submitting ? (
                  <CircularProgress color="secondary" size={20} />
                ) : (
                  <SaveIcon />
                )
              }
              disabled={fetching || submitting}
              onClick={handleTransformAndSubmit}
            >
              Save All
            </Button>
          </Grid>
        </Grid>
      </CollapsiblePanel>
      <CollapsiblePanel title="Preview & Update ข้อมูลสินค้า" defaultCollapse>
        <PreviewContainers />
      </CollapsiblePanel>

      {editModal.open && (
        <MappingRuleModal
          open={editModal.open}
          defaultValue={editModal.data}
          handleCloseModal={handleCloseEditModal}
          handleSubmitModal={handleSubmitEditModal}
        />
      )}
    </>
  );
};

export default ListContainers;
